// Per-page SEO metadata.
//
// React 19 hoists `<title>`, `<meta>` and `<link>` rendered anywhere in the tree
// into `<head>`, so this component works without react-helmet. It keeps the head
// correct during client side navigation.
//
// Crawlers that do not execute JavaScript are served the same tags baked into the
// static HTML by `scripts/prerender-blog.mjs` at build time.

export const SITE_URL = 'https://checker.podus.org';
export const SITE_NAME = 'YourChecka';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SeoProps {
    title: string;
    description: string;
    /** Path (e.g. `/blog/my-post`) or absolute URL. */
    canonicalPath: string;
    image?: string | null | undefined;
    type?: 'website' | 'article';
    /** Any schema.org object. Rendered as JSON-LD. */
    jsonLd?: Record<string, unknown> | null;
    noIndex?: boolean;
}

function absolute(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    return `${SITE_URL}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

export default function Seo({
    title,
    description,
    canonicalPath,
    image,
    type = 'website',
    jsonLd,
    noIndex = false,
}: SeoProps) {
    const url = absolute(canonicalPath);
    const ogImage = image ? absolute(image) : DEFAULT_OG_IMAGE;

    return (
        <>
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={url} />
            {noIndex && <meta name="robots" content="noindex, nofollow" />}

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={ogImage} />

            {jsonLd && (
                <script
                    type="application/ld+json"
                    // Structured data is generated from our own content, never user input.
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
                />
            )}
        </>
    );
}
